'use client';

import { memo, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import Image from 'next/image';
import { ArrowRight } from 'lucide-react';
import {
  portalGames as defaultGames,
  portalCharacters as characters,
  type PortalGame,
} from '../lib/portal-data';
import {
  fetchPortalCalendar,
  fetchPortalNews,
  resolveActiveCalendarDate,
  type PortalCalendarEvent,
  type PortalNewsItem,
} from '../lib/portal-feed';
import './portal-home.css';
import './portal-v3.css';

type Preview = { title: string; text: string; image: string; label: string };
type Props = {
  games?: PortalGame[];
  onGame: (game: PortalGame) => void;
  onPreview: (preview: Preview) => void;
  onOpenWikis?: (sort: 'popular' | 'latest') => void;
  onOpenUpdates?: () => void;
  onOpenCharacters?: () => void;
};

function SectionHeading({
  title,
  subtitle,
  href,
  onOpen,
}: {
  title: string;
  subtitle?: string;
  href: string;
  onOpen?: () => void;
}) {
  return (
    <div className="pv3-section-heading">
      <div className="pv3-heading-title">
        <h2>{title}</h2>
        {subtitle ? <span>{subtitle}</span> : null}
      </div>
      <a
        href={href}
        className="pv3-view-all"
        onClick={(event) => {
          if (!onOpen) return;
          if (
            event.button !== 0 ||
            event.metaKey ||
            event.ctrlKey ||
            event.shiftKey ||
            event.altKey
          ) {
            return;
          }
          event.preventDefault();
          onOpen();
        }}
      >
        查看全部 <ArrowRight size={14} aria-hidden="true" />
      </a>
    </div>
  );
}

function PortalHome({
  games = defaultGames,
  onGame,
  onPreview,
  onOpenWikis,
  onOpenUpdates,
  onOpenCharacters,
}: Props) {
  const [tab, setTab] = useState<'birthday' | 'arrival'>('birthday');
  const [selectedGameIndex, setSelectedGameIndex] = useState(0);
  const [isRankingHovered, setIsRankingHovered] = useState(false);
  const swipeStart = useRef<{ x: number; y: number } | null>(null);
  const suppressSwipeClick = useRef(false);
  const [calendarEvents, setCalendarEvents] = useState<PortalCalendarEvent[]>(
    [],
  );
  const [newsItems, setNewsItems] = useState<PortalNewsItem[]>([]);

  const rankingGames = useMemo(() => {
    return [...games]
      .sort((a, b) => {
        const hotDiff = (b.hot ?? 0) - (a.hot ?? 0);
        if (hotDiff !== 0) return hotDiff;
        return (b.collectedAt || b.collected || '').localeCompare(
          a.collectedAt || a.collected || '',
        );
      })
      .slice(0, 6);
  }, [games]);

  const selectedGame =
    rankingGames[
      rankingGames.length === 0
        ? 0
        : Math.min(selectedGameIndex, rankingGames.length - 1)
    ];

  useEffect(() => {
    if (selectedGameIndex < rankingGames.length) return;
    setSelectedGameIndex(0);
  }, [rankingGames.length, selectedGameIndex]);

  useEffect(() => {
    if (isRankingHovered || rankingGames.length <= 1) return;
    const timer = window.setInterval(() => {
      setSelectedGameIndex((value) => (value + 1) % rankingGames.length);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [isRankingHovered, rankingGames.length]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      fetchPortalCalendar({ page: 1, pageSize: 10 }),
      fetchPortalNews({ page: 1, pageSize: 5 }),
    ]).then(([calendar, news]) => {
      if (cancelled) return;
      setCalendarEvents(calendar.items);
      setNewsItems(news.items);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const popularIds = useMemo(() => {
    const ranked = [...games].sort((a, b) => {
      const hotDiff = (b.hot ?? 0) - (a.hot ?? 0);
      if (hotDiff !== 0) return hotDiff;
      return (b.collectedAt || b.collected || '').localeCompare(
        a.collectedAt || a.collected || '',
      );
    });
    // 与游戏 Wiki「热门」一致：热度前 20 不进最新 wiki
    return new Set(ranked.slice(0, 20).map((game) => game.id));
  }, [games]);

  const latestGames = useMemo(
    () =>
      [...games]
        .filter((game) => !popularIds.has(game.id))
        .sort((a, b) =>
          (b.collectedAt || b.collected || '').localeCompare(
            a.collectedAt || a.collected || '',
          ),
        )
        .slice(0, 18),
    [games, popularIds],
  );

  const calendarMonthLabel = useMemo(() => {
    const raw = calendarEvents[0]?.eventDate || '';
    const match = raw.match(/^(\d{4})-(\d{2})/);
    return match ? `${match[1]} / ${match[2]}` : '近期日程';
  }, [calendarEvents]);

  const todayCalendarDate = useMemo(
    () => resolveActiveCalendarDate(calendarEvents),
    [calendarEvents],
  );

  const characterCards = [...characters].sort((a, b) =>
    tab === 'birthday'
      ? a.birthday.localeCompare(b.birthday)
      : b.arrival.localeCompare(a.arrival),
  );

  const gameLink = (
    game: PortalGame,
    className: string,
    children: ReactNode,
  ) =>
    game.href ? (
      <a
        href={game.href}
        key={game.id}
        className={className}
        {...(game.external
          ? { target: '_blank', rel: 'noopener noreferrer' }
          : {})}
      >
        {children}
      </a>
    ) : (
      <button key={game.id} className={className} onClick={() => onGame(game)}>
        {children}
      </button>
    );

  return (
    <div className="portal-home-board portal-v3-page">
      <div className="portal-width pv3-main">
        <section className="pv3-section pv3-wiki-section">
          <SectionHeading
            title="头部WIKI"
            href="/wikis"
            onOpen={onOpenWikis ? () => onOpenWikis('popular') : undefined}
          />
          <div
            className="pv3-featured-layout"
            onTouchStart={(event) => {
              const touch = event.touches[0];
              swipeStart.current = { x: touch.clientX, y: touch.clientY };
              suppressSwipeClick.current = false;
            }}
            onTouchEnd={(event) => {
              const start = swipeStart.current;
              swipeStart.current = null;
              if (!start || rankingGames.length < 2) return;
              const touch = event.changedTouches[0];
              const dx = touch.clientX - start.x;
              const dy = touch.clientY - start.y;
              if (Math.abs(dx) < 40 || Math.abs(dx) <= Math.abs(dy)) return;
              suppressSwipeClick.current = true;
              setSelectedGameIndex((index) =>
                (index + (dx < 0 ? 1 : -1) + rankingGames.length) % rankingGames.length,
              );
            }}
            onTouchCancel={() => { swipeStart.current = null; }}
            onClickCapture={(event) => {
              if (!suppressSwipeClick.current) return;
              suppressSwipeClick.current = false;
              event.preventDefault();
              event.stopPropagation();
            }}
          >
            {selectedGame ? (
              gameLink(
                selectedGame,
                'pv3-featured-card',
                <>
                  <img
                    src={selectedGame.banner || selectedGame.image}
                    alt={selectedGame.name}
                  />
                  <div className="pv3-featured-overlay">
                    <span className="pv3-featured-tag">本周焦点</span>
                    <h3>{selectedGame.name}</h3>
                    <span className="pv3-featured-entry">
                      进入 Wiki <ArrowRight size={17} />
                    </span>
                  </div>
                </>,
              )
            ) : (
              <div className="pv3-featured-card pv3-online-empty" aria-hidden="true" />
            )}
            <div className="pv3-dots" aria-label="焦点轮播">
              {rankingGames.map((game, index) => (
                <button
                  type="button"
                  key={game.id}
                  className={index === selectedGameIndex ? 'is-active' : ''}
                  onClick={() => setSelectedGameIndex(index)}
                  aria-label={`切换到${game.name}`}
                />
              ))}
            </div>
            <aside
              className="pv3-ranking"
              aria-label="热门 Wiki"
              onMouseEnter={() => setIsRankingHovered(true)}
              onMouseLeave={() => setIsRankingHovered(false)}
            >
              {rankingGames.map((game, index) => {
                const className = `pv3-ranking-item${index === selectedGameIndex ? ' is-selected' : ''}`;
                const body = (
                  <>
                    <b className={index < 3 ? 'is-top' : ''}>
                      {String(index + 1).padStart(2, '0')}
                    </b>
                    <img src={game.image} alt="" />
                    <span>
                      <strong>{game.name}</strong>
                      <small>图鉴 · 攻略 · 社区</small>
                    </span>
                    <ArrowRight size={14} />
                  </>
                );
                if (game.href) {
                  return (
                    <a
                      href={game.href}
                      key={game.id}
                      className={className}
                      onMouseEnter={() => setSelectedGameIndex(index)}
                      onFocus={() => setSelectedGameIndex(index)}
                      {...(game.external
                        ? { target: '_blank', rel: 'noopener noreferrer' }
                        : {})}
                    >
                      {body}
                    </a>
                  );
                }
                return (
                  <button
                    type="button"
                    key={game.id}
                    className={className}
                    onMouseEnter={() => setSelectedGameIndex(index)}
                    onFocus={() => setSelectedGameIndex(index)}
                    onClick={() => onGame(game)}
                  >
                    {body}
                  </button>
                );
              })}
            </aside>
          </div>

          <div className="pv3-latest">
            <SectionHeading
              title="最新wiki"
              href="/wikis"
              onOpen={onOpenWikis ? () => onOpenWikis('latest') : undefined}
            />
            <div className="pv3-game-grid">
              {latestGames.map((game) =>
                gameLink(
                  game,
                  'pv3-game-item',
                  <>
                    <img src={game.image} alt="" />
                    <strong title={game.name}>{game.name}</strong>
                    <small>{game.collected || '\u00a0'}</small>
                  </>,
                ),
              )}
            </div>
          </div>
        </section>

        <section className="pv3-section pv3-news-section">
          <SectionHeading
            title="游戏资讯"
            href="/updates"
            onOpen={onOpenUpdates}
          />
          <div className="pv3-news-layout">
            <div className="pv3-news-list">
              <h3>最新资讯</h3>
              {newsItems.map((item) => (
                <button
                  type="button"
                  className="pv3-news-item"
                  key={item.id}
                  onClick={() =>
                    onPreview({
                      title: item.title,
                      text: item.body,
                      image: item.coverUrl,
                      label: `${item.date} · ${item.tag || '资讯'}`,
                    })
                  }
                >
                  <img src={item.coverUrl} alt="" />
                  <span>
                    <strong>{item.title}</strong>
                    <small>
                      {item.gameName}
                      <time>{item.date}</time>
                    </small>
                  </span>
                </button>
              ))}
            </div>
            <div className="pv3-calendar">
              <div className="pv3-subheading">
                <h3>游戏日历</h3>
                <span>{calendarMonthLabel}</span>
              </div>
              <div className="pv3-calendar-grid">
                {calendarEvents.map((item) => {
                  const isToday = item.eventDate === todayCalendarDate;
                  return (
                  <button
                    type="button"
                    className={`pv3-calendar-item${isToday ? ' is-today' : ''}`}
                    key={item.id}
                    onClick={() =>
                      onPreview({
                        title: `${item.gameName} · ${item.eventType}`,
                        text: item.detail,
                        image: item.coverUrl,
                        label: `${item.eventDate.replace(/-/g, '.')} · 游戏日程`,
                      })
                    }
                  >
                    <time dateTime={item.eventDate}>
                      {item.eventDate.slice(5).replace('-', '.')}
                      <i />
                    </time>
                    <img src={item.coverUrl} alt="" />
                    <strong>{item.gameName || item.title}</strong>
                    <small>{item.eventType}</small>
                  </button>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        <section className="ph-section">
          <div className="ph-heading">
            <h2>角色动态</h2>
            <p>记录每一次相遇 · 虚构角色展示</p>
            <a
              href="/characters"
              onClick={(event) => {
                if (!onOpenCharacters) return;
                if (
                  event.button !== 0 ||
                  event.metaKey ||
                  event.ctrlKey ||
                  event.shiftKey ||
                  event.altKey
                ) {
                  return;
                }
                event.preventDefault();
                onOpenCharacters();
              }}
            >
              查看全部 <ArrowRight size={14} />
            </a>
          </div>
          <div className="ph-character-tabs" aria-label="角色动态分类">
            <button
              aria-pressed={tab === 'birthday'}
              onClick={() => setTab('birthday')}
            >
              最近生日
            </button>
            <button
              aria-pressed={tab === 'arrival'}
              onClick={() => setTab('arrival')}
            >
              最近实装
            </button>
          </div>
          <div className="ph-character-mosaic">
            {[0, 1, 2].map((column) => (
              <div className="ph-character-column" key={column}>
                {characterCards
                  .slice(0, 5)
                  .filter((_, index) => index % 3 === column)
                  .map((item, row) => {
                    const index = column + row * 3;
                    return (
                      <button
                        className="ph-character-card"
                        key={item.id}
                        onClick={() =>
                          onPreview({
                            title: item.name,
                            text: item.description,
                            image: item.image,
                            label: `${item.game} · ${tab === 'birthday' ? `${item.birthday} 生日` : `${item.arrival} 实装`} · 虚构角色`,
                          })
                        }
                      >
                        <span
                          className={`ph-character-cover ph-character-cover-${(index % 3) + 1}`}
                        >
                          <Image
                            unoptimized
                            fill
                            sizes="(max-width: 760px) 100vw, (max-width: 1050px) 50vw, 390px"
                            src={item.image}
                            alt={`${item.name}角色插画`}
                            style={{ objectPosition: item.position }}
                          />
                        </span>
                        <div className="ph-character-meta">
                          <span>
                            <h3>{item.name}</h3>
                            <small>{item.game}</small>
                          </span>
                          <time
                            className={tab === 'arrival' ? 'ph-arrival' : ''}
                          >
                            {tab === 'birthday'
                              ? `${item.birthday} 生日`
                              : `${item.arrival} 实装`}
                          </time>
                          <ArrowRight size={16} />
                        </div>
                      </button>
                    );
                  })}
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

export default memo(PortalHome);
